import React, { useState, useRef, useEffect } from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { cn } from '@/lib/utils';

interface MagicCardProps {
  children: React.ReactNode;
  className?: string;
  containerClassName?: string;
  glowColor?: string;
  borderGlowColor?: string;
}

export const MagicCard = ({
  children,
  className,
  containerClassName,
  glowColor = 'rgba(0, 0, 255, 0.15)',
  borderGlowColor = 'rgba(0, 0, 255, 0.4)',
}: MagicCardProps) => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const x = useMotionValue(0.5);
  const y = useMotionValue(0.5);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      setPrefersReducedMotion(mediaQuery.matches);

      const handleChange = (e: MediaQueryListEvent) => {
        setPrefersReducedMotion(e.matches);
      };

      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, []);

  const mouseXSpring = useSpring(x, { stiffness: 300, damping: 30 });
  const mouseYSpring = useSpring(y, { stiffness: 300, damping: 30 });

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (prefersReducedMotion || !containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;

    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    const xPct = mouseX / width;
    const yPct = mouseY / height;

    x.set(xPct);
    y.set(yPct);
  };

  const handleMouseLeave = () => {
    if (prefersReducedMotion) return;
    x.set(0.5);
    y.set(0.5);
  };

  const gradientXPercent = useTransform(mouseXSpring, [0, 1], [0, 100]);
  const gradientYPercent = useTransform(mouseYSpring, [0, 1], [0, 100]);

  return (
    <motion.div
      ref={containerRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className={cn('relative group', containerClassName)}
    >
      <motion.div
        className={cn(
          'relative h-full overflow-hidden rounded-xl bg-slate-900 border border-slate-800',
          'transition-all duration-300',
          'hover:border-blue-500/50 hover:scale-[1.01]',
          className
        )}
      >
        {/* Glow effect on hover - follows mouse */}
        {!prefersReducedMotion && (
          <motion.div
            className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none rounded-xl"
            style={{
              background: useTransform(
                gradientXPercent,
                (x) => `radial-gradient(circle 400px at ${x}% 50%, #0000FF 0%, #000000 70%)`
              ),
            }}
          />
        )}

        {/* Border glow on hover */}
        {!prefersReducedMotion && (
          <motion.div
            className="absolute -inset-[1px] opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none rounded-xl blur-sm"
            style={{
              background: useTransform(
                gradientXPercent,
                (x) => `radial-gradient(circle 300px at ${x}% 50%, #0000FF 0%, #000000 60%)`
              ),
            }}
          />
        )}

        {/* Content */}
        <div className="relative z-10 h-full">{children}</div>
      </motion.div>
    </motion.div>
  );
};
